/* eslint-disable no-nested-ternary */
import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { isMnrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { useLagoon } from '../../../hooks/useLagoon';
import { convertDisplayNameToTitleCase } from '../../../utils/common';
import {
  getFullName,
  getInitials,
  menuItems,
} from '../../../utils/psxHeader.utils';
import { getCurrentMember } from '../../../utils/user.utils';
import { Constants } from '../../Constants';
import { phoneOnly } from '../../ConstantsStyles';
import { showMemberSelector } from '../../Utils';
import { MemberSelection } from '../MemberSelection';
import { WelcomeMessageScreen } from '../MemberSelection/MemberSelection.styled';
import { MemberInfo } from '../ObapiDemo/memberProfile';
import {
  Avatar,
  WelcomeMessage,
  WelcomeMessageContainer,
  WelcomeMessageName,
  WelcomeMessageNameContainer,
} from './MemberSelectionHeader.styled';

type Props = {
  activeUser: MemberInfo;
  form: any;
  memberProfiles: MemberInfo[];
  setActiveMemberIndex: (a: number) => void;
};

export const MemberSelectionHeader = ({
  activeUser,
  form,
  memberProfiles,
  setActiveMemberIndex,
}: Props) => {
  const { t } = useTranslation();
  const { LOGGED_IN_MEMBER_INDEX } = Constants.STORAGE_KEYS.SESSION;
  const loggedInMemberIndex = storage.session.get(LOGGED_IN_MEMBER_INDEX);
  const loggedInMember = memberProfiles[loggedInMemberIndex];
  const activeUserFullName = getFullName(activeUser);
  const mobileScreen = useMediaQuery(phoneOnly);

  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const disableMemberSelection = find(featureFlags, {
    key: ConstantsLagoon.FEATURE_FLAGS.DISABLE_MEMBER_SELECTION,
  })?.active;

  const welcomeMessageText = !activeUser?.demographics?.name?.firstName
    ? t('Find care')
    : t("Let's find care for");

  const loggedInMemberRelationshipTypecode =
    loggedInMember?.demographics?.relationshipTypeCode?.code;

  return (
    <WelcomeMessageContainer
      data-auto-testid="search-home-message-container"
      data-testid="search-home-message-container"
    >
      {activeUser?.demographics?.name?.firstName ? (
        <Avatar>
          <div>{getInitials(activeUserFullName)}</div>
        </Avatar>
      ) : null}
      <WelcomeMessageNameContainer
        data-auto-testid="search-home-name-container"
        data-testid="search-home-name-container"
      >
        <WelcomeMessage
          data-auto-testid="search-home-welcome-message"
          data-testid="search-home-welcome-message"
        >
          <WelcomeMessageName
            data-auto-testid="search-home-message-name"
            data-testid="search-home-message-name"
          >
            <div>
              <Text>{welcomeMessageText}</Text>
              {showMemberSelector(
                loggedInMemberRelationshipTypecode,
                memberProfiles,
                isMnrMember(getCurrentMember()?.lineOfBusiness),
                disableMemberSelection
              ) ? (
                <MemberSelection
                  data-auto-testid="member-selection-modal"
                  data-testid="member-selection-modal"
                  form={form}
                  menuItems={menuItems(memberProfiles)}
                  name={convertDisplayNameToTitleCase(
                    activeUser?.demographics?.name?.firstName
                  )}
                  setActiveMemberIndex={setActiveMemberIndex}
                />
              ) : mobileScreen ? (
                <WelcomeMessageScreen>
                  {convertDisplayNameToTitleCase(
                    activeUser?.demographics?.name?.firstName
                  )}
                </WelcomeMessageScreen>
              ) : (
                <Text>
                  {convertDisplayNameToTitleCase(
                    activeUser?.demographics?.name?.firstName
                  )}
                </Text>
              )}
            </div>
          </WelcomeMessageName>
          <span id="memberSelectionButton" style={{ display: 'none' }}>
            Change member name
          </span>
        </WelcomeMessage>
      </WelcomeMessageNameContainer>
    </WelcomeMessageContainer>
  );
};
